import { Chip, Icon, useIsMobile } from '@epilot/concorde-elements'
import type { EnumButtonProps, IconConfig } from '@epilot/journey-logic-commons'
import { appendStepBlockId, clsx } from '@epilot/journey-logic-commons'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ChoiceButton,
  ChoiceWrapper,
  getWrapperClassName,
  IconLabel
} from '../components'
import classes from '../components/Choice/ChoiceButton.module.scss'

type Option =
  | string
  | {
      value: string
      label: string
      icon?: IconConfig
    }

export const EnumButtonControl = (props: EnumButtonProps) => {
  const {
    handleChange,
    path,
    data,
    options,
    hasError,
    label,
    isRequired,
    defaultSelect,
    id,
    buttonType
  } = props

  const { t } = useTranslation()

  const isMobile = useIsMobile()

  const handleOnChange = (value: string | undefined) => {
    const newValue = !value && defaultSelect ? defaultSelect : value

    handleChange(path, newValue)
  }

  const buttons = options.map((option: Option) => {
    const label = typeof option === 'string' ? option : option.label
    const value = typeof option === 'string' ? option : option.value

    const fieldId = appendStepBlockId(id, label)
    const isSelected = data === value

    return (
      <Fragment key={value}>
        {buttonType === 'chip' ? (
          <Chip
            aria-describedby={hasError ? `${id}-errorMessage` : undefined}
            aria-selected={isSelected}
            backgroundColor={'var(--concorde-neutral-surface)'}
            className={clsx(
              'Concorde-SingleChoice-Chip-Block',
              classes.chip,
              isSelected && classes.selectedChip,
              isSelected && 'Concorde-SingleChoice-Chip__Selected'
            )}
            id={fieldId}
            leftIcon={
              <Icon
                className={clsx(
                  isSelected ? classes.iconVisible : classes.iconHidden
                )}
                isFilled={isSelected}
                name="check"
              />
            }
            onClick={() => handleOnChange(value === data ? undefined : value)}
            role="option"
          >
            <IconLabel
              className={
                isSelected ? classes.selectedChipLabel : classes.chipLabel
              }
              iconConfig={typeof option === 'string' ? undefined : option.icon}
              label={label}
            />
          </Chip>
        ) : (
          <ChoiceButton
            aria-describedby={hasError ? `${id}-errorMessage` : undefined}
            aria-selected={isSelected}
            className={clsx(
              isSelected && 'Concorde-SingleChoice-Button__Selected'
            )}
            id={fieldId}
            isSelected={isSelected}
            label={
              <IconLabel
                iconConfig={
                  typeof option === 'string' ? undefined : option.icon
                }
                label={label}
              />
            }
            onClick={() => handleOnChange(value === data ? undefined : value)}
            role="option"
          />
        )}
      </Fragment>
    )
  })

  return (
    <ChoiceWrapper
      className="Concorde-SingleChoice-Button-Block"
      hasError={hasError}
      id={id}
      isRequired={isRequired}
      label={label}
    >
      <div
        aria-label={label || t('Please select one of the options')}
        className={getWrapperClassName(buttonType, isMobile)}
        role="listbox"
      >
        {buttons}
      </div>
    </ChoiceWrapper>
  )
}
